import {
  Box,
  BoxProps,
  Button,
  ButtonGroup,
  Center,
  Divider,
  HStack,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  TabList,
  Tabs,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import React from "react";
import { useNavigate } from "react-router";
import { ColumnKeyEnum } from "../../constants";
import {
  ICohort,
  ICohortIndividualCreate,
  ICohortIndividualSelected,
} from "../../routes/registries/models/Cohort";
import { IRegistry } from "../../routes/registries/models/Registry";
import { useAddQuickPin } from "../../routes/registries/stores/actions/useAddQuickPin";
import { useRemoveQuickPinById } from "../../routes/registries/stores/actions/useRemoveQuickPinById";
import { useSetRegistryId } from "../../routes/registries/stores/actions/useSetRegistryId";
import { IQuickPin } from "../../routes/registries/stores/models/RegistryLayoutStore";
import { useSelectIsEditMode } from "../../routes/registries/stores/select/useSelectIsEditMode";
import { useSelectQuickPins } from "../../routes/registries/stores/select/useSelectQuickPins";

import { useListCohorts } from "../../services/api/cohorts/queries/useListCohorts";
import { useListRegistries } from "../../services/api/registries/queries/useListRegistries";
import { MaterialIcon } from "../shared/MaterialIcon";
import { AddToCohortModal } from "./AddToCohortModal";
import { QuickPin } from "./QuickPin";
import { RemoveFromCohortModal } from "./RemoveFromCohortModal";

interface IProps extends BoxProps {
  registry?: IRegistry;
  cohort?: ICohort;
  gridRef: any;
  isGridReady: boolean;
}

export const QuickNavigator = ({
  registry,
  cohort,
  gridRef,
  isGridReady,
  ...rest
}: IProps) => {
  const quickPins = useSelectQuickPins();
  const addQuickPin = useAddQuickPin();
  const isEditMode = useSelectIsEditMode();
  const setRegistryId = useSetRegistryId();
  const removeQuickPinById = useRemoveQuickPinById();

  const navigate = useNavigate();

  const [allowOpenCohorts, setAllowOpenCohorts] = React.useState<ICohort[]>([]);
  const [suggestedPin, setSuggestedPin] = React.useState<string>("");
  const [allowOpenRegistries, setAllowOpenRegistries] = React.useState<
    IRegistry[]
  >([]);

  const [cohortIndividuals, setCohortIndividuals] = React.useState<
    ICohortIndividualCreate[]
  >([]);
  const [cohortSelectedIndividuals, setCohortSelectedIndividuals] =
    React.useState<ICohortIndividualSelected[]>([]);

  const {
    isOpen: isOpenAddToCohortModal,
    onOpen: onOpenAddToCohortModal,
    onClose: onCloseAddToCohortModal,
  } = useDisclosure();

  const {
    isOpen: isOpenRemoveFromCohortModal,
    onOpen: onOpenRemoveFromCohortModal,
    onClose: onCloseRemoveFromCohortModal,
  } = useDisclosure();

  const { data: dataCohorts, isLoading: isLoadingCohorts } = useListCohorts();
  const { data: dataRegistries, isLoading: isLoadingRegistries } =
    useListRegistries();

  const toast = useToast();

  const handleOpenCohort = (cohort: ICohort) => {
    navigate(`/app/cohorts/${cohort.id}`);
  };

  const handleOpenRegistry = (registry: IRegistry) => {
    navigate(`/app/registries/${registry.id}`);
  };

  const handleOnAddQuickPin = () => {
    let targetPathUrl = "";
    let targetTitle = "";

    if (registry) {
      targetTitle = registry.title;
      targetPathUrl = `/app/registries/${registry.id}`;
    }

    if (cohort) {
      targetTitle = cohort.title;
      targetPathUrl = `/app/cohorts/${cohort.id}`;
    }

    if (targetTitle && targetPathUrl) {
      addQuickPin({
        targetPath: targetPathUrl,
        title: targetTitle,
      });

      toast({
        title: "Pinned!",
        description: `Added ${targetTitle} to your pins.`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleOnAddCohort = () => {
    onOpenAddToCohortModal();
  };

  const handleOnRemoveCohort = () => {
    onOpenRemoveFromCohortModal();
  };

  const handleUpdateAllowOpenRegistries = React.useCallback(() => {
    let allowOpenRegistriesUpdate: IRegistry[] = [];

    if (dataRegistries) {
      allowOpenRegistriesUpdate = dataRegistries.filter(
        (registryIn) => registryIn.id !== registry?.id
      );
    }

    setAllowOpenRegistries(allowOpenRegistriesUpdate);
  }, [registry, dataRegistries]);

  const handleUpdateAllowOpenCohorts = React.useCallback(() => {
    let allowOpenCohortsUpdate: ICohort[] = [];

    if (dataCohorts) {
      allowOpenCohortsUpdate = dataCohorts.filter(
        (cohortIn) => cohortIn.id !== cohort?.id
      );
    }

    setAllowOpenCohorts(allowOpenCohortsUpdate);
  }, [dataCohorts, cohort]);

  const handleOnRemoveSuccess = () => {
    // Reset buttons
    setCohortIndividuals([]);
    setCohortSelectedIndividuals([]);
    // Close modal
    onCloseRemoveFromCohortModal();
  };

  const handleOnRemoveQuickPin = (quickPin: IQuickPin) => {
    removeQuickPinById({ quickPinPath: quickPin.targetPath });
    toast({
      title: "Unpinned!",
      description: `Removed ${quickPin.title} from your pins.`,
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const onSelectionChanged = React.useCallback(() => {
    let selectedRowsUpdate = [];

    if (gridRef?.current?.api) {
      selectedRowsUpdate = gridRef.current.api.getSelectedRows();
    }

    const cohortIndividualsUpdate: ICohortIndividualCreate[] = [];
    const cohortSelectedIndividualsUpdate: ICohortIndividualSelected[] = [];

    if (selectedRowsUpdate) {
      selectedRowsUpdate.forEach((row: any) => {
        let registryIdIn = registry?.id;

        if (!registryIdIn) {
          registryIdIn = row[ColumnKeyEnum.registry_id];
        }

        if (registryIdIn) {
          cohortIndividualsUpdate.push({
            registry_individual_id: row[ColumnKeyEnum.record_id],
            registry_id: registryIdIn,
          });

          if (cohort?.id) {
            cohortSelectedIndividualsUpdate.push({
              individual_name: `${row[ColumnKeyEnum.first_name]} ${
                row[ColumnKeyEnum.last_name]
              }`,
              registry_name: row[ColumnKeyEnum.registry_title],
              cohort_individual_id: row[ColumnKeyEnum.cohort_individual_id],
            });
          }
        }
      });
    }

    setCohortIndividuals(cohortIndividualsUpdate);
    setCohortSelectedIndividuals(cohortSelectedIndividualsUpdate);
  }, [gridRef, registry, cohort]);

  React.useEffect(() => {
    handleUpdateAllowOpenCohorts();
  }, [dataCohorts, handleUpdateAllowOpenCohorts]);

  React.useEffect(() => {
    handleUpdateAllowOpenRegistries();
  }, [dataRegistries, handleUpdateAllowOpenRegistries]);

  React.useEffect(() => {
    if (registry) {
      setRegistryId(registry.id);
    }
    setCohortIndividuals([]);
    setCohortSelectedIndividuals([]);
  }, [setRegistryId, registry, cohort]);

  React.useEffect(() => {
    let suggestedPinUpdate = "";

    if (cohort) {
      suggestedPinUpdate = cohort.title;
    }
    if (registry) {
      suggestedPinUpdate = registry.title;
    }

    setSuggestedPin(suggestedPinUpdate);
    handleUpdateAllowOpenRegistries();
    handleUpdateAllowOpenCohorts();
  }, [
    cohort,
    registry,
    handleUpdateAllowOpenCohorts,
    handleUpdateAllowOpenRegistries,
  ]);

  React.useEffect(() => {
    const gridApi = gridRef?.current?.api;

    if (gridApi) {
      gridApi.addEventListener("rowSelected", onSelectionChanged);
    }

    return () => {
      if (gridApi) {
        gridApi.removeEventListener("rowSelected", onSelectionChanged);
      }
    };
  }, [onSelectionChanged, isGridReady, gridRef]);

  return (
    <>
      <Box {...rest}>
        <Tabs variant={"soft-rounded"} py={2} flexWrap="wrap">
          <TabList
            gap={"4"}
            borderColor={"transparent"}
            h="full"
            flexWrap="wrap"
            alignItems={"center"}
          >
            <Menu>
              <MenuButton
                as={IconButton}
                // variant="outline"
                size="md"
                disabled={isEditMode}
                // colorScheme="teal"
                // fontSize="lg"
                aria-label="Open registry"
                icon={
                  <MaterialIcon
                    name="diversity_2"
                    variant="outline"
                    fontSize="1.5rem"
                  />
                }
              ></MenuButton>
              <MenuList>
                {allowOpenRegistries.map((registry) => (
                  <MenuItem
                    key={registry.id}
                    onClick={() => handleOpenRegistry(registry)}
                  >
                    <HStack w="full" py={2}>
                      <Center w={16}>
                        {registry.registry_brand_url && (
                          <Box h="8" rounded="sm" overflow={"hidden"}>
                            <Image
                              objectFit={"contain"}
                              w="full"
                              h="full"
                              src={registry.registry_brand_url}
                            ></Image>
                          </Box>
                        )}

                        {!registry.registry_brand_url && (
                          <MaterialIcon
                            color="text.primary"
                            name="diversity_2"
                            fontSize="2rem"
                          />
                        )}
                      </Center>
                      <Text>{registry.title}</Text>
                    </HStack>
                  </MenuItem>
                ))}
                {!isLoadingRegistries &&
                  allowOpenRegistries.length === 0 &&
                  dataRegistries &&
                  dataRegistries?.length > 0 && (
                    <Text color="text.soft" py={4} px={2}>
                      All your Registries are open
                    </Text>
                  )}
              </MenuList>
            </Menu>

            <Menu>
              <MenuButton
                as={IconButton}
                // variant="outline"
                size="md"
                disabled={isEditMode}
                // colorScheme="teal"
                // fontSize="lg"
                aria-label="Open cohort"
                icon={
                  <MaterialIcon
                    name="join_inner"
                    variant="outline"
                    fontSize="1.5rem"
                  />
                }
              ></MenuButton>
              <MenuList>
                {allowOpenCohorts.map((cohort) => (
                  <MenuItem
                    key={cohort.id}
                    onClick={() => handleOpenCohort(cohort)}
                  >
                    <HStack w="full" py={2}>
                      <Center w={16}>
                        <MaterialIcon
                          color="text.primary"
                          name="join_inner"
                          fontSize="2rem"
                        />
                      </Center>
                      <Text>{cohort.title}</Text>
                    </HStack>
                  </MenuItem>
                ))}
                {!isLoadingCohorts &&
                  allowOpenCohorts.length === 0 &&
                  dataCohorts &&
                  dataCohorts?.length > 0 && (
                    <Text color="text.soft" py={4} px={2}>
                      All your cohorts are open
                    </Text>
                  )}
                {!isLoadingCohorts && dataCohorts && dataCohorts?.length === 0 && (
                  <Text color="text.soft" py={4} px={2}>
                    You don't have any cohorts yet
                  </Text>
                )}
              </MenuList>
            </Menu>

            <Divider
              h="1.5rem"
              w="2px"
              bg="bg.highest"
              orientation="vertical"
            ></Divider>

            {quickPins &&
              Object.entries(quickPins).map(([quickPinId, quickPin], idx) => (
                <QuickPin
                  key={quickPinId}
                  quickPin={quickPin}
                  onRemove={() => handleOnRemoveQuickPin(quickPin)}
                ></QuickPin>
              ))}

            <Tooltip label={`Pin ${suggestedPin}`}>
              <IconButton
                aria-label="Add quick pin"
                onClick={handleOnAddQuickPin}
                variant="ghost"
                icon={
                  <MaterialIcon
                    name="push_pin"
                    variant="outline"
                    fontSize="1rem"
                  />
                }
              ></IconButton>
            </Tooltip>

            {/* Add new button displayed when individuals are selected */}
            <ButtonGroup size="md">
              {cohortIndividuals && cohortIndividuals.length > 0 && (
                <Button
                  disabled={isEditMode}
                  colorScheme={"primary"}
                  leftIcon={
                    <MaterialIcon
                      name="join_inner"
                      variant="outline"
                      fontSize="1.5rem"
                    />
                  }
                  onClick={() => handleOnAddCohort()}
                >
                  Add to cohort ({cohortIndividuals.length})
                </Button>
              )}
              {/* Add remove button displayed when individuals are selected */}
              {cohortSelectedIndividuals &&
                cohortSelectedIndividuals.length > 0 && (
                  <Button
                    colorScheme={"orange"}
                    onClick={() => handleOnRemoveCohort()}
                    leftIcon={
                      <MaterialIcon
                        name="person_remove"
                        variant="outline"
                        fontSize="1.5rem"
                      />
                    }
                  >
                    Remove from cohort
                  </Button>
                )}
            </ButtonGroup>
          </TabList>
        </Tabs>
      </Box>
      <AddToCohortModal
        isOpen={isOpenAddToCohortModal}
        onClose={onCloseAddToCohortModal}
        individuals={cohortIndividuals}
        excludeCohortId={cohort?.id}
      ></AddToCohortModal>
      {cohort && (
        <RemoveFromCohortModal
          isOpen={isOpenRemoveFromCohortModal}
          onClose={onCloseRemoveFromCohortModal}
          onSuccess={handleOnRemoveSuccess}
          individuals={cohortSelectedIndividuals}
          cohort={cohort}
        ></RemoveFromCohortModal>
      )}
    </>
  );
};
